import { Grid } from '@mui/material';
import { ReactNode, useContext } from 'react';
import styles from "../../../assets/styles/componentStyles/chatbotStyles/chats.module.scss";
import { ACTION_TYPE_NAME } from '../../../components/dashboard/flowBuilder/flowBuilderConstants';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import { currentActiveThreadChatbotThreadData } from '../../../models/axiosModel';
import { formatDateAccordingToTimezoneUsingMoment } from '../../../utils/constants';
import { renderAgentChips } from '../../../pages/ruleBasedInbox/utils';
import { renderFormattedMsg } from '../renderUtils';
import { AGENT_ACTIVITY_ACTION_TYPE, CHATBOT_ACTION_TYPE, INSTAGRAM_ACTION_TYPES, MISCELLANEOUS_TYPES, TIMEOUT_TRIGGER_EXECUTION_STATUS } from '../constants';

export const getCenterSystemMessageState = (thread: currentActiveThreadChatbotThreadData) => {
    const actionTypeName = thread?.action_type_name?.toLowerCase() ?? "";

    const isAgentInvolved = (actionTypeName === AGENT_ACTIVITY_ACTION_TYPE['ASSIGN'] || actionTypeName === AGENT_ACTIVITY_ACTION_TYPE['JOIN'] || actionTypeName === AGENT_ACTIVITY_ACTION_TYPE['LEFT']);
    const isChatbotClosed = actionTypeName === CHATBOT_ACTION_TYPE["CLOSED"]?.toLowerCase();
    const isChatbotDeployed = actionTypeName === CHATBOT_ACTION_TYPE["DEPLOYED"]?.toLowerCase();
    const isChatbotReDeployed = actionTypeName === CHATBOT_ACTION_TYPE["REDEPLOYED"]?.toLowerCase();
    const timeoutTriggerExecutionAction = actionTypeName === ACTION_TYPE_NAME["TIMEOUT_TRIGGER_EXECUTION"] && thread?.action_execution_data?.trigger_execution_status_name?.toLowerCase() === TIMEOUT_TRIGGER_EXECUTION_STATUS['EXECUTED']?.toLowerCase();
    const igCommentReceivedActionType = actionTypeName === INSTAGRAM_ACTION_TYPES['IG_COMMENT_RECEIVED']?.toLowerCase();
    const replyToIgCommentActionType = actionTypeName === INSTAGRAM_ACTION_TYPES['REPLY_TO_IG_COMMENT']?.toLowerCase();
    const isActionTypeMiscellaneous = actionTypeName === ACTION_TYPE_NAME["MISCELLANEOUS"]?.toLowerCase();
    const isMiscTypeText = isActionTypeMiscellaneous && thread?.action_execution_data?.misc_type?.toLowerCase() === MISCELLANEOUS_TYPES['TEXT']?.toLowerCase();
    const isArchiveChatActionType = actionTypeName === ACTION_TYPE_NAME["ARCHIVE_CHAT"]?.toLowerCase();
    const isChatbotLifecycleAction = isChatbotClosed || isChatbotDeployed || isChatbotReDeployed;
    const showSystemMsgContainer = isChatbotLifecycleAction || igCommentReceivedActionType || replyToIgCommentActionType || isActionTypeMiscellaneous || isArchiveChatActionType;
    const shouldRenderMessagesInCenter = isAgentInvolved || timeoutTriggerExecutionAction || showSystemMsgContainer;

    return {
        isAgentInvolved,
        isChatbotClosed,
        isChatbotDeployed,
        isChatbotReDeployed,
        timeoutTriggerExecutionAction,
        igCommentReceivedActionType,
        replyToIgCommentActionType,
        isActionTypeMiscellaneous,
        isMiscTypeText,
        isArchiveChatActionType,
        isChatbotLifecycleAction,
        showSystemMsgContainer,
        shouldRenderMessagesInCenter,
    };
}

export type CenterSystemMessageState = ReturnType<typeof getCenterSystemMessageState>;

const getChatbotLifecycleStatusLabel = (isChatbotClosed: boolean, isChatbotDeployed: boolean) => {
    if (isChatbotClosed) return 'closed';
    if (isChatbotDeployed) return 'deployed';
    return 'redeployed';
}

const getSystemMessageBodyContent = (
    thread: currentActiveThreadChatbotThreadData,
    state: CenterSystemMessageState
): ReactNode => {
    if (state.isActionTypeMiscellaneous) {
        return (
            <Grid item className={styles.systemWarningMsgText}>
                {thread?.action_execution_data?.misc_value || ''}
            </Grid>
        );
    }

    let messageContent: ReactNode;
    if (state.isChatbotLifecycleAction) {
        messageContent = `${thread?.chatbot_data?.chatbot_name || ""} Chatbot has been ${getChatbotLifecycleStatusLabel(state.isChatbotClosed, state.isChatbotDeployed)}`;
    } else if (state.isArchiveChatActionType) {
        messageContent = `Chat has been ${thread?.action_execution_data?.flag_archive ? 'archived' : 'unarchived'}`;
    } else if (state.isAgentInvolved){
        messageContent = renderAgentChips(thread);
    } else if (state.timeoutTriggerExecutionAction){
        messageContent = "Smart Delay has been Executed";
    } else {
        messageContent = (
            <span className={styles.igCommentChipMessage}>
                {state.igCommentReceivedActionType ? 'User Comment' : 'Comment Reply'}:{' '}
                {renderFormattedMsg(thread?.action_execution_data?.comment_message ?? "")}
            </span>
        );
    }

    return <Grid item>{messageContent}</Grid>;
} 

const shouldShowMsgTimestamp = (state: CenterSystemMessageState) => {
    return !state.timeoutTriggerExecutionAction
}

interface SystemMessageProps {
    thread: currentActiveThreadChatbotThreadData;
    agentChipBgColor: string;
    state: CenterSystemMessageState;
}

const SystemMessage = ({ thread, agentChipBgColor, state }: SystemMessageProps) => {
    const { userDetail } = useContext(UserContext) as UserContextPayload;

    return (
        <Grid item className={styles.userMessageGrid}>
            <Grid container justifyContent={"center"} px={1}>
                <Grid item className={styles.assignAgentMessageInnerGrid} sx={{ background: agentChipBgColor }}>
                    <Grid container direction={"row"} className={styles.systemMsgContainer}>
                        {getSystemMessageBodyContent(thread, state)}
                        {shouldShowMsgTimestamp(state) && 
                            <Grid item className={styles.chatbotClosedRedeployChipTimeStamp}>
                                {formatDateAccordingToTimezoneUsingMoment(thread.timestamp, userDetail.timezone_hour)}
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default SystemMessage;
